import React, { useRef, useState } from 'react';
import {
  FormControl,
  FormHelperText,
  OutlinedInput,
  Popper,
  Paper,
  List,
  ListItemButton,
  FormLabel
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { ValidationProps } from '@models/app-autocomplete.model.ts';

type Suggestion = { label: string; value: string };

type Props = {
  name: string;
  label: React.ReactNode;
  placeholder?: string;
  control: any;
  parseInput?: (value: string) => Suggestion[];
  validationProps?: ValidationProps;
  disabled?: boolean;
};

const pluralize = (num: number, singular: string, plural: string) =>
  num === 1 ? singular : plural;

const defaultParseInput = (value: string): Suggestion[] => {
  value = value.trim().toLowerCase();
  const suggestions: Suggestion[] = [];

  const m = value.match(/^(\d+)\s*(m|min|minutes?)$/i);
  if (m) {
    const minVal = parseInt(m[1], 10);
    return [
      {
        label: `${minVal} ${pluralize(minVal, 'minute', 'minutes')}`,
        value: `${minVal}m`
      }
    ];
  }

  const h = value.match(/^(\d+)\s*(h|hr|hours?)$/i);
  if (h) {
    const hrVal = parseInt(h[1], 10);
    return [
      {
        label: `${hrVal} ${pluralize(hrVal, 'hour', 'hours')}`,
        value: `${hrVal}h`
      }
    ];
  }

  const fullMatch = value.match(
    /(\d+(\.\d+)?)\s*(h|hr|hours?)?\s*(\d+)?\s*(m|min|minutes?)?/i
  );
  if (fullMatch) {
    let hours = parseFloat(fullMatch[1]) || 0;
    let minutes = 0;

    if (hours % 1 !== 0) {
      minutes = Math.round((hours % 1) * 60);
      hours = Math.floor(hours);
    }

    if (fullMatch[4]) {
      minutes = parseInt(fullMatch[4], 10);
    }

    const label = `${hours > 0 ? hours + ' ' + pluralize(hours, 'hour', 'hours') : ''}${
      hours > 0 && minutes > 0 ? ' ' : ''
    }${minutes > 0 ? minutes + ' ' + pluralize(minutes, 'minute', 'minutes') : ''}`;

    const valueStr = `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
    suggestions.push({ label, value: valueStr });
    return suggestions;
  }

  const num = value.match(/^(\d+(\.\d+)?)$/);
  if (num) {
    const floatVal = parseFloat(num[1]);
    const hours = Math.floor(floatVal);
    const minutes = Math.round((floatVal % 1) * 60);

    if (hours === 0 && minutes > 0) {
      return [
        {
          label: `${minutes} ${pluralize(minutes, 'minute', 'minutes')}`,
          value: `${minutes}m`
        }
      ];
    }

    const label = `${hours > 0 ? hours + ' ' + pluralize(hours, 'hour', 'hours') : ''}${
      hours > 0 && minutes > 0 ? ' ' : ''
    }${minutes > 0 ? minutes + ' ' + pluralize(minutes, 'minute', 'minutes') : ''}`;

    const valueStr = `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
    return [{ label, value: valueStr }];
  }

  return [];
};

const TimeEstimateInput: React.FC<Props> = ({
  name,
  label,
  placeholder = '',
  control,
  parseInput = defaultParseInput,
  validationProps,
  disabled = false
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const blurTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    errors,
    errorMessages,
    validationRules = {},
    getErrorMessage
  } = validationProps || {};

  const openPopover = (target: HTMLElement, value: string) => {
    const parsed = parseInput(value);
    setSuggestions(parsed);
    setAnchorEl(parsed.length > 0 ? target : null);
  };

  const closePopover = () => {
    setAnchorEl(null);
    setSuggestions([]);
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={validationRules}
      render={({ field }) => (
        <>
          <FormControl error={!!errors?.[name]} fullWidth disabled={disabled}>
            <FormLabel>{label}</FormLabel>
            <OutlinedInput
              {...field}
              inputRef={inputRef}
              placeholder={placeholder}
              autoComplete="off"
              disabled={disabled}
              value={field.value ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                field.onChange(val);
                openPopover(e.currentTarget, val);
              }}
              onFocus={(e) => {
                openPopover(e.currentTarget, e.target.value);
                if (blurTimeout.current) clearTimeout(blurTimeout.current);
              }}
              onBlur={() => {
                field.onBlur();
                blurTimeout.current = setTimeout(() => {
                  closePopover();
                }, 150);
              }}
            />
            {validationProps && (
              <FormHelperText>
                {errors?.[name] &&
                  getErrorMessage?.({
                    errors,
                    fieldName: name,
                    errorMessages,
                    validationRules
                  })}
              </FormHelperText>
            )}
          </FormControl>

          <Popper
            open={!!anchorEl && suggestions.length > 0}
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{ zIndex: 1300 }}
          >
            <Paper
              elevation={3}
              style={{ minWidth: inputRef.current?.offsetWidth }}
            >
              <List dense>
                {suggestions.map((s, i) => (
                  <ListItemButton
                    key={i}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      field.onChange(s.value);
                      closePopover();
                      inputRef.current?.blur();
                    }}
                  >
                    {s.label}
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Popper>
        </>
      )}
    />
  );
};

export default TimeEstimateInput;
